import './ProductReviews.css';
import { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getProductReviews } from '../services/reviewService';

const { FiChevronLeft, FiChevronRight } = FiIcons;

function ProductReviews({ productId }) {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!productId) return;
    getProductReviews(productId).then(setReviews).catch(() => {});
  }, [productId]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
      if (event.target.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      if (event.key === 'ArrowLeft') {
        setActiveIndex((index) => Math.max(0, index - 1));
      }
      if (event.key === 'ArrowRight') {
        setActiveIndex((index) => Math.min(reviews.length - 1, index + 1));
      }
    };
    document.addEventListener('keydown', closeOnEscape);
    document.body.classList.add('review-dialog-open');
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.body.classList.remove('review-dialog-open');
    };
  }, [open, reviews.length]);

  if (!reviews.length) return null;
  return (
    <>
      <button
        type="button"
        className="product-review-link"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="product-review-link-stars" aria-hidden="true">★★★★★</span>
        +{reviews.length} {reviews.length === 1 ? 'review' : 'reviews'} on this item · Click to view
      </button>
      {open && (
        <div
          className="product-reviews-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setOpen(false);
          }}
        >
          <div className="product-reviews-dialog" role="dialog" aria-modal="true" aria-label="Product reviews">
            <div className="product-reviews-dialog-header">
              <div>
                <span className="product-reviews-eyebrow">ZEVI CUSTOMER STORIES</span>
                <h2>Reviews for this piece</h2>
              </div>
              <button
                type="button"
                className="product-reviews-close"
                onClick={() => setOpen(false)}
                aria-label="Close reviews"
              >
                ×
              </button>
            </div>
            <section className="zevi-reviews" aria-live="polite">
              <h2>Customer stories</h2>
              <div className="zevi-review-grid">
                {reviews.map((review, index) => (
                  <article key={review.id} hidden={activeIndex !== index}>
                    <span aria-label={`${review.rating} out of 5 stars`}>
                      {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                    </span>
                    <h3>{review.customer_name}</h3>
                    {review.comment && <p>{review.comment}</p>}
                    {review.image_url && <img src={review.image_url} alt={`Review by ${review.customer_name}`} loading="lazy" />}
                  </article>
                ))}
              </div>
            </section>
            <div className="product-review-carousel-controls">
              <button
                type="button"
                className="product-review-arrow"
                onClick={() => setActiveIndex((index) => Math.max(0, index - 1))}
                disabled={activeIndex === 0}
                aria-label="Previous review"
              >
                <SafeIcon icon={FiChevronLeft} />
              </button>
              <span className="product-review-position">
                {activeIndex + 1} <span>/</span> {reviews.length}
              </span>
              <button
                type="button"
                className="product-review-arrow"
                onClick={() => setActiveIndex((index) => Math.min(reviews.length - 1, index + 1))}
                disabled={activeIndex === reviews.length - 1}
                aria-label="Next review"
              >
                <SafeIcon icon={FiChevronRight} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ProductReviews;